import { Backdrop } from '@latitude/backdrop';
import type { ButtonAction } from '@latitude/footer';
import type { Content } from '@radix-ui/react-dialog';
import { Description, Title } from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import type { VariantProps } from 'class-variance-authority';
import { Body } from 'components/Body';
import { Footer, type FooterProps } from 'components/Footer';
import { Header, type HeaderProps } from 'components/Header';
import type { yPosOption } from 'components/Root';
import Root from 'components/Root';
import { useAnimatedHeight } from 'hooks/useAnimatedHeight';
import useModal from 'hooks/useModal';
import type { ComponentPropsWithoutRef, FC, ReactNode } from 'react';

import type { modalVariants } from './variants/modal-variants';

/**
 * Props for the Modal component.
 */
type ModalProps = VariantProps<typeof modalVariants> &
  HeaderProps &
  Omit<FooterProps, 'tertiaryAction'> & {
    /** Whether the modal is open or not. */
    isOpen: boolean;

    /** Size of the modal.
     * @default 'medium'
     */
    size?: 'small' | 'medium' | 'large';

    /** Content of the modal. */
    children?: ReactNode;

    onInteractOutside?: ComponentPropsWithoutRef<
      typeof Content
    >['onInteractOutside'];

    /**
     * Runs before Escape closes the modal. Call `e.preventDefault()` to keep it
     * open — the only way to hold a multi-step flow open when a stray keystroke
     * would discard work the user cannot recover.
     */
    onEscapeKeyDown?: ComponentPropsWithoutRef<
      typeof Content
    >['onEscapeKeyDown'];

    loading?: boolean;
    leadingElement?: ReactNode;
    yPosition?: yPosOption;

    /**
     * Transitions the body's height when its content grows or shrinks. Opt in for
     * bodies that mount and unmount fields: a centered dialog otherwise moves both
     * edges in one frame and drags any popover anchored inside it along.
     * @default false
     */
    animateHeight?: boolean;
    classNames?: {
      body?: string;
      content?: string;
    };
    'data-testid'?: string;
  };

/**
 * Configuration for a button action. Owned by `@latitude/footer` and
 * re-exported here so existing `import type { ButtonAction } from
 * '@latitude/modal'` keeps working.
 */
export type { ButtonAction };

const Modal: FC<ModalProps> = ({
  isOpen,
  title,
  subTitle = undefined,
  size = 'medium',
  children = undefined,
  onClose,
  leadingAction = undefined,
  primaryAction = undefined,
  yPosition,
  secondaryAction = undefined,
  loading = false,
  onInteractOutside,
  onEscapeKeyDown,
  leadingElement,
  hideCloseButton = false,
  truncateTitle = false,
  progress = undefined,
  headerProps = undefined,
  footerProps = undefined,
  animateHeight = false,
  classNames,
  'data-testid': testId,
}) => {
  const renderHeader = Boolean(
    title || subTitle || onClose || headerProps?.title || headerProps?.subtext,
  );

  const { bodyRef, setMeasureRef } = useAnimatedHeight();

  return (
    <Root
      isOpen={isOpen}
      onInteractOutside={onInteractOutside}
      onEscapeKeyDown={onEscapeKeyDown}
      onClose={onClose}
      size={size}
      contentClassName={classNames?.content}
      yPosition={yPosition}
      data-testid={testId}
    >
      {renderHeader ? (
        <Header
          title={title}
          subTitle={subTitle}
          onClose={onClose}
          leadingElement={leadingElement}
          hideCloseButton={hideCloseButton}
          truncateTitle={truncateTitle}
          progress={progress}
          headerProps={headerProps}
        />
      ) : (
        <>
          <VisuallyHidden>
            <Title>{'Untitled'}</Title>
            <Description>{'Untitled'}</Description>
          </VisuallyHidden>
        </>
      )}
      <Body
        ref={bodyRef}
        animateHeight={animateHeight}
        className={classNames?.body}
      >
        <div className="relative size-full">
          {/* The measured box: `size-full` makes every other candidate report the
              body's own height back to itself, hiding shrinkage, and `flow-root`
              keeps a child's margin inside it. It must stay padding- and
              border-free: the hook reads the entry's `contentRect`, which excludes
              both. Opt-in only — see CLAUDE.md. */}
          {animateHeight ? (
            <div ref={setMeasureRef} className="flow-root">
              {children}
            </div>
          ) : (
            children
          )}
          {loading && (
            <Backdrop
              loading={loading}
              classNames={{ backdrop: 'absolute top-0' }}
            />
          )}
        </div>
      </Body>
      {(leadingAction || primaryAction || secondaryAction || footerProps) && (
        <Footer
          leadingAction={leadingAction}
          primaryAction={primaryAction}
          secondaryAction={secondaryAction}
          footerProps={footerProps}
        />
      )}
    </Root>
  );
};

Modal.displayName = 'Modal';

export { DiscardChangesModal } from './presets/DiscardChangesModal';
export type { DiscardChangesModalProps } from './presets/DiscardChangesModal';

export {
  Modal,
  Body as ModalBody,
  Footer as ModalFooter,
  Header as ModalHeader,
  Root as ModalRoot,
  useModal,
  type ModalProps,
};
